import { useEffect, useState } from "react";
import type { Facets } from "../api";

export type Filters = {
  symbols: string[]; // empty = all
  timeframes: string[];
  directions: string[];
  origins: string[];
  min_score: number;
  tradable: boolean;
  tight: boolean;
};

export const DEFAULT_FILTERS: Filters = {
  symbols: [],
  timeframes: [],
  directions: [],
  origins: ["manual"],
  min_score: 0,
  tradable: false,
  tight: false,
};

export default function FilterDrawer({
  open,
  onClose,
  facets,
  value,
  onChange,
}: {
  open: boolean;
  onClose: () => void;
  facets: Facets | undefined;
  value: Filters;
  onChange: (next: Filters) => void;
}) {
  const [local, setLocal] = useState<Filters>(value);
  useEffect(() => setLocal(value), [value, open]);

  if (!open) return null;
  const f = facets ?? {
    symbols: [],
    timeframes: [],
    directions: ["bull", "bear"],
    origins: [],
    max_score: 100,
  };

  const apply = () => {
    onChange(local);
    onClose();
  };
  const reset = () => setLocal(DEFAULT_FILTERS);

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-handle" />
        <h3 style={{ margin: "0 0 4px" }}>Filters</h3>

        <ChipGroup
          label="Origin"
          options={f.origins}
          selected={local.origins}
          onToggle={(v) =>
            setLocal((s) => ({
              ...s,
              origins: toggle(s.origins, v),
            }))
          }
        />
        <ChipGroup
          label="Timeframe"
          options={f.timeframes}
          selected={local.timeframes}
          onToggle={(v) =>
            setLocal((s) => ({
              ...s,
              timeframes: toggle(s.timeframes, v),
            }))
          }
          emptyHint="all"
        />
        <ChipGroup
          label="Direction"
          options={["bull", "bear"]}
          selected={local.directions}
          onToggle={(v) =>
            setLocal((s) => ({
              ...s,
              directions: toggle(s.directions, v),
            }))
          }
          emptyHint="both"
        />

        <ChipGroup
          label="Symbol"
          options={f.symbols}
          selected={local.symbols}
          onToggle={(v) =>
            setLocal((s) => ({
              ...s,
              symbols: toggle(s.symbols, v),
            }))
          }
          emptyHint="all"
          scroll
        />

        <div className="drawer-row">
          <label className="muted" style={{ minWidth: 100 }}>
            Min score
          </label>
          <input
            type="range"
            min={0}
            max={Math.max(f.max_score, 1)}
            step={1}
            value={local.min_score}
            onChange={(e) =>
              setLocal((s) => ({ ...s, min_score: Number(e.target.value) }))
            }
            style={{ flex: 1 }}
          />
          <span style={{ minWidth: 36, textAlign: "right" }}>
            {local.min_score.toFixed(0)}
          </span>
        </div>

        <label className="drawer-toggle">
          <input
            type="checkbox"
            checked={local.tradable}
            onChange={(e) =>
              setLocal((s) => ({ ...s, tradable: e.target.checked }))
            }
          />
          Tradable only (no breakout, still active)
        </label>
        <label className="drawer-toggle">
          <input
            type="checkbox"
            checked={local.tight}
            onChange={(e) => setLocal((s) => ({ ...s, tight: e.target.checked }))}
          />
          Tight range only
        </label>

        <div className="drawer-actions">
          <button onClick={reset}>Reset</button>
          <button className="primary" onClick={apply} style={{ flex: 1 }}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

function toggle<T>(arr: T[], v: T): T[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

function ChipGroup({
  label,
  options,
  selected,
  onToggle,
  emptyHint,
  scroll,
}: {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
  emptyHint?: string;
  scroll?: boolean;
}) {
  if (options.length === 0) return null;
  const empty = selected.length === 0;
  return (
    <div className="drawer-row col">
      <div className="muted small">
        {label}
        {empty && emptyHint ? ` · ${emptyHint}` : ""}
      </div>
      <div className={"chips" + (scroll ? " chips-scroll" : "")}>
        {options.map((o) => {
          const on = selected.includes(o);
          return (
            <button
              key={o}
              className={"chip" + (on ? " chip-on" : "")}
              onClick={() => onToggle(o)}
              type="button"
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
